import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {toSignal} from '@angular/core/rxjs-interop';
import {IntegrationService} from '@app/services/integration.service';
import {ActivePortalsService} from '@app/services/util/active-portals.service';
import {BusinessUnitsService} from '@app/services/accounts/business-units.service';
import {portalLogoFor} from '@app/shared/constants/portal-logos';
import {IntegrationsNames} from '@app/enums/IntegrationTypeEnum';

/**
 * Fallback display name for a portal absent from `IntegrationsNames` (kept
 * deliberately unbranded elsewhere, e.g. the Quick Entry flow — BRIEFD-7047 —
 * but this indicator names it explicitly since that's the whole point here).
 */
const FALLBACK_PORTAL_NAMES: Record<string, string> = {
  avtonet: 'Avtonet',
};

interface PortalRow {
  type: string;
  name: string;
  logoSrc: string | null;
}

/**
 * BRIEFD-7195: reusable "which portals use this field" indicator, shown below
 * a vehicle Advertising field. Given a field's static `usedByPortals` /
 * `notUsedByPortals` capability lists (from the BE field config), cross-
 * references the account's ACTIVE integrations and renders:
 *  - logos for portals that are both connected AND actually use the field;
 *  - an explicit note for each connected portal the backend named as NOT
 *    using it (BRIEFD-7202's specific case: Avtonet and the ad title).
 *
 * Renders nothing when the field declares no portal metadata, or when none
 * of those portals are connected.
 */
@Component({
  selector: 'app-field-portal-usage',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './field-portal-usage.component.html',
  styleUrls: ['./field-portal-usage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldPortalUsageComponent {
  private readonly integrationService = inject(IntegrationService);
  private readonly activePortals = inject(ActivePortalsService);
  private readonly businessUnits = inject(BusinessUnitsService);

  /** Integration type keys (e.g. 'njuskalo') that actually use this field. */
  readonly usedByPortals = input<string[] | undefined>();
  /** Integration type keys the backend named as NOT using this field. */
  readonly notUsedByPortals = input<string[] | undefined>();
  /** Market the surrounding UI is scoped to (a pricing/description flag tab).
   *  Country-bound portals from another market are dropped: on the Croatian
   *  flag, "Cena uvoza" is read by Njuškalo and Index, never by Bolha
   *  (BRIEFD-7359). */
  readonly country = input<string | null>(null);
  /** The dealer's own market. Portals bound to no market of their own
   *  (Facebook, Virtual Salon, Quick Entry) advertise into it — AdFieldsHelper
   *  ::getIntegrationCountryOfOrigin() says the same on the backend — so they
   *  belong on the own-country flag rather than repeating under every one.
   *  Without it they survive every filter, which is what a caller with no flag
   *  tabs wants. */
  readonly ownCountry = input<string | null>(null);

  /** Scoped to the selected business unit — without it the response omits every
   *  `per_business_unit` portal (Njuškalo, Bolha, Index.hr, Shopaj24), which is
   *  the whole reason this indicator used to render nothing. Only reached when
   *  no vehicle published its own portals, e.g. the create form (BRIEFD-7359). */
  private readonly integrations = toSignal(
    this.integrationService.getIntegrations(null, false, this.businessUnits.getStoredBusinessUnitId()),
    {initialValue: {data: []}},
  );

  /** The vehicle's own active portals when a vehicle form published them,
   *  otherwise the business-unit-scoped integrations list (BRIEFD-7359). */
  private readonly connectedTypes = computed(() => {
    const published = this.activePortals.active();
    if (published) {
      return new Set(published.map((p) => p.key));
    }
    return new Set(
      (this.integrations().data ?? [])
        .filter((integration) => integration.isActive)
        .map((integration) => integration.type?.toLowerCase()),
    );
  });

  /** The market a portal publishes into — always the backend's answer, never a
   *  copy kept here (BRIEFD-7359): `PlatformStatusDto::country` when a vehicle
   *  published its portals, else `marketCountry` off the integrations list, both
   *  of which resolve through `IntegrationEnum::PORTAL_MARKET`. Null = bound to
   *  no market, which `rowsFor()` resolves to the dealer's own. */
  private marketOf(type: string): string | null {
    const published = this.activePortals.active()?.find((p) => p.key === type);
    if (published) { return published.country; }

    const integration = (this.integrations().data ?? [])
      .find((i) => i.type?.toLowerCase() === type);

    return integration?.marketCountry ?? null;
  }

  private static toRow(type: string): PortalRow {
    return {
      type,
      name: IntegrationsNames[type] ?? FALLBACK_PORTAL_NAMES[type] ?? type,
      logoSrc: portalLogoFor(type),
    };
  }

  /** Connected, and — when the UI is scoped to a market — publishing into it. */
  private readonly rowsFor = (portals: string[] | undefined): PortalRow[] => {
    const connected = this.connectedTypes();
    const scoped = (this.country() ?? '').toLowerCase();
    const own = (this.ownCountry() ?? '').toLowerCase();
    const publishesInto = (type: string): boolean => {
      if (!scoped) { return true; }
      const market = this.marketOf(type) ?? (own || null);
      return market === null || market === scoped;
    };
    return (portals ?? [])
      .map((type) => type.toLowerCase())
      .filter((type) => connected.has(type))
      .filter(publishesInto)
      .map((type) => FieldPortalUsageComponent.toRow(type));
  };

  readonly usedPortalRows = computed<PortalRow[]>(() => this.rowsFor(this.usedByPortals()));

  readonly excludedPortalRows = computed<PortalRow[]>(() => this.rowsFor(this.notUsedByPortals()));

  readonly hasAnythingToShow = computed(
    () => this.usedPortalRows().length > 0 || this.excludedPortalRows().length > 0,
  );
}
